
import React, { Component, ErrorInfo, ReactNode } from "react";
import "../styles/error-handling.css";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.group('%c🚨 COMPONENT ERROR DETECTED', 'color: #ff0000; font-weight: bold; font-size: 16px; background: #fff; padding: 4px;');
    
    // Enhanced error logging
    console.error('Error message:', error.message);
    console.error('Error name:', error.name);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);
    
    // Extract failed component information
    const failedComponent = this.extractFailedComponent(error, errorInfo);
    const filePath = this.extractFilePath(error);
    const importError = this.detectImportError(error);
    
    if (failedComponent) {
      console.error(`🎯 Failed component: ${failedComponent}`);
    }
    
    if (filePath) {
      console.error(`📁 File path: ${filePath}`);
    }
    
    if (importError) {
      console.error(`📦 Import issue detected: ${importError}`);
    }
    
    // Log browser and environment info
    console.info('Browser info:', {
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    });
    
    console.groupEnd();
    
    this.setState({ errorInfo });
    this.logErrorToService(error, errorInfo);
  }
  
  private extractFailedComponent(error: Error, errorInfo: ErrorInfo): string | null {
    // Try to extract from component stack first
    if (errorInfo?.componentStack) {
      const componentMatch = errorInfo.componentStack.match(/^\s*in\s+([A-Z][a-zA-Z0-9_$]*)/m);
      if (componentMatch?.[1]) {
        return componentMatch[1];
      }
    }
    
    // Try to extract from error stack
    if (error?.stack) {
      const componentMatch = error.stack.match(/at\s+([A-Z][a-zA-Z0-9_$]+)(?:\.|\.render)/);
      if (componentMatch?.[1]) {
        return componentMatch[1];
      }
    }
    
    return null;
  }
  
  private extractFilePath(error: Error): string | null {
    if (!error?.stack) return null;
    
    // Look for file paths in the stack
    const pathMatches = error.stack.match(/(src\/[^:)?\s]+)/g);
    if (pathMatches?.length) {
      return pathMatches[0];
    }
    
    // Look for chunk loading errors
    const chunkMatch = error.stack.match(/Loading\s+chunk\s+\d+\s+failed/);
    if (chunkMatch) {
      return 'Dynamic import chunk loading failed';
    }
    
    return null;
  }
  
  private detectImportError(error: Error): string | null {
    const message = error.message.toLowerCase();
    
    if (message.includes('loading chunk') || message.includes('chunk load failed')) {
      return 'Dynamic import chunk loading failed - possible network or bundling issue';
    }
    
    if (message.includes('module not found') || message.includes('cannot resolve')) {
      return 'Module resolution failed - file may not exist or path is incorrect';
    }
    
    if (message.includes('unexpected token') && message.includes('<')) {
      return 'MIME type error - JavaScript module served as HTML';
    }
    
    if (message.includes('default') && message.includes('export')) {
      return 'Missing default export in imported module';
    }
    
    return null;
  }
  
  private logErrorToService(error: Error, errorInfo: ErrorInfo) {
    // Enhanced error logging for debugging
    const errorData = {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      failedComponent: this.extractFailedComponent(error, errorInfo),
      filePath: this.extractFilePath(error),
      importError: this.detectImportError(error)
    };
    
    console.log("📊 Detailed error data:", errorData);
  }
  
  public resetError = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="error-container">
          <h2 className="error-title">Error al cargar el componente</h2>
          <p className="text-white/70 mb-4">
            No se pudo cargar la página solicitada.
          </p>
          {this.state.error && (
            <div className="error-message">
              <strong>Error:</strong> {this.state.error.message || "Error desconocido"}
              {this.extractFailedComponent(this.state.error, this.state.errorInfo!) && (
                <div className="mt-2">
                  <strong>Componente:</strong> {this.extractFailedComponent(this.state.error, this.state.errorInfo!)}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-4 mt-6">
            <button 
              onClick={() => window.location.reload()} 
              className="error-reload-btn"
            >
              Recargar página
            </button>
            <button 
              onClick={() => window.history.back()} 
              className="error-back-btn"
            >
              Volver atrás
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
